// Model and agent output read as text (DESIGN-SPEC §3.4): a one-line snippet or a list
// row never shows markdown syntax. The markdown is parsed, not pattern-matched, and read
// back as its words: a link reads as its label, a table as its cells, and code, images
// and link definitions are left out.
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';

// Markdown that is not prose.
const skippedNodes = new Set([
  'code',
  'image',
  'imageReference',
  'definition',
  'footnoteDefinition',
]);
const blockNodes = new Set([
  'paragraph',
  'heading',
  'listItem',
  'blockquote',
  'tableCell',
  'break',
  'thematicBreak',
]);

type MarkdownNode = {
  type: string;
  value?: string;
  children?: MarkdownNode[];
  position?: { start: { offset?: number } };
};

const isSpace = (character: string) => character.trim() === '';
function collapseSpace(text: string) {
  let out = '';
  for (const character of text) {
    if (!isSpace(character)) out += character;
    else if (out && !out.endsWith(' ')) out += ' ';
  }
  return out.trimEnd();
}

// Extracted PDF text starts every page with "--- Page N of T ---" on its own line
// (src/company-workspace/data/pdf-text.mjs); it is structure, not content.
const isPdfPageMarker = (line: string) =>
  line.trim().startsWith('--- Page ') && line.trim().endsWith(' ---');

/** The words of a markdown text on one line, as a person reads them. */
export function markdownPlainText(markdown: string, { pdf = false } = {}) {
  const tree = unified().use(remarkParse).use(remarkGfm).parse(markdown) as MarkdownNode;
  let text = '';
  const visit = (node: MarkdownNode) => {
    if (skippedNodes.has(node.type)) return;
    const block = blockNodes.has(node.type);
    if (block) text += ' ';
    if (typeof node.value === 'string')
      text += pdf
        ? node.value
            .split('\n')
            .filter((line) => !isPdfPageMarker(line))
            .join(' ')
        : node.value;
    node.children?.forEach(visit);
    if (block) text += ' ';
  };
  visit(tree);
  return collapseSpace(text).trim();
}

const parse = (text: string) =>
  unified().use(remarkParse).use(remarkGfm).parse(text) as MarkdownNode;

// The server marks a text it had to shorten with a closing ellipsis (inbox/service.mjs
// cut(), agents/run-text.mjs clip()), so the last block is the one the cut landed in.
const CUT = '…';
const hasContent = (block: MarkdownNode) =>
  block.type !== 'heading' && block.type !== 'thematicBreak';

/**
 * A shortened markdown preview without its broken end: the block the cut landed in (a
 * table that lost its rows, a link that lost its target) is left out whenever what
 * comes before it still says something. `cut` is true when the text was shortened.
 */
export function completeMarkdown(text: string): { text: string; cut: boolean } {
  const trimmed = text.trimEnd();
  if (!trimmed.endsWith(CUT)) return { text, cut: false };
  const blocks = parse(trimmed).children ?? [];
  const start = blocks.at(-1)?.position?.start.offset;
  const kept = blocks.slice(0, -1);
  if (start === undefined || !kept.some(hasContent)) return { text: trimmed, cut: true };
  return { text: trimmed.slice(0, start).trimEnd(), cut: true };
}

/**
 * A name or title that may carry inline markdown (a proposed fact is model text), read as
 * its words: "**Free** returns" reads "Free returns". Text that parses as anything but one
 * paragraph ("1. Q4 plan", "# 3") is a name, not markdown, and stays exactly as written.
 */
export function inlinePlainText(text: string) {
  const blocks = parse(text).children ?? [];
  if (blocks.length !== 1 || blocks[0].type !== 'paragraph') return text;
  const words = (node: MarkdownNode): string =>
    node.type === 'image'
      ? ''
      : typeof node.value === 'string'
        ? node.value
        : (node.children ?? []).map(words).join('');
  return words(blocks[0]).trim() || text;
}
